import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Injector,
  OnInit,
  computed,
  inject,
  runInInjectionContext,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import type { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { of } from 'rxjs';
import { EducationLevel } from '../../../api/models/education-level';
import { SchoolType } from '../../../api/models/school-type';
import { EducationLevelService } from '../../../api/services/education-level.service';

type LoadFn = (ctx: { injector: Injector }) => Observable<EducationLevel[]>;

const BANDS = [SchoolType.Middelbaar, SchoolType.Vervolgonderwijs] as const;

const BAND_STYLES: Record<string, { header: string; body: string }> = {
  [SchoolType.Middelbaar]: {
    header: 'linear-gradient(120deg, #1e5ea0 0%, #357bbf 55%, #4a8ecb 100%)',
    body: 'linear-gradient(180deg, rgba(53,123,191,0.055) 0%, rgba(53,123,191,0.02) 100%)',
  },
  [SchoolType.Vervolgonderwijs]: {
    header: 'linear-gradient(120deg, #1290bf 0%, #29b9e3 55%, #3ecdf5 100%)',
    body: 'linear-gradient(180deg, rgba(41,185,227,0.055) 0%, rgba(41,185,227,0.02) 100%)',
  },
};

@Component({
  selector: 'app-formly-education-level-selector',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, TranslateModule],
  template: `
    <div class="flex flex-col gap-5">
      @for (band of bands; track band) {
        @if (getRegionsForBand(band).length > 0) {
          <div class="rounded-[20px] overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.04),0_3px_12px_rgba(0,0,0,0.06)]">

            <!-- Band header -->
            <div class="flex items-center justify-between px-5 py-3"
                 [style.background]="bandStyles[band].header">
              <span class="font-alternate text-[13.5px] font-semibold text-white tracking-[0.01em]">
                {{ band | translate }}
              </span>
              @if (selectedCountForBand(band) > 0) {
                <span class="inline-flex items-center justify-center min-w-[20px] h-[20px] px-[7px] rounded-full bg-white/[0.22] text-white/95 font-alternate text-[11px] font-semibold transition-transform">
                  {{ selectedCountForBand(band) }}
                </span>
              }
            </div>

            <!-- Band body: dynamic N-column grid based on actual region count -->
            <div class="grid" [style.grid-template-columns]="'repeat(' + getRegionsForBand(band).length + ', minmax(0, 1fr))'" [style.background]="bandStyles[band].body">

              <!-- Region headers -->
              @for (region of getRegionsForBand(band); track region; let last = $last) {
                <div [class]="'pt-3 pb-2 text-center font-alternate text-[10px] font-semibold tracking-[0.14em] uppercase text-black/35'
                              + (last ? '' : ' border-r border-black/[0.07]')">
                  {{ region }}
                </div>
              }

              <!-- Divider below headers (spans all columns) -->
              <div class="mx-3 mb-2 h-px bg-black/[0.07]" style="grid-column: 1 / -1"></div>

              <!-- One row per unique level — aligned across all regions -->
              @for (levelNum of uniqueLevelsForBand(band); track levelNum) {
                @for (region of getRegionsForBand(band); track region; let last = $last) {
                  <div [class]="'px-3 pt-[3px]' + (last ? '' : ' border-r border-black/[0.07]')">
                    @if (getLevelInRegion(band, region, levelNum); as level) {
                      <button
                        class="block w-full px-[11px] py-[6px] mb-[5px] rounded-full text-[12.5px] font-medium leading-[1.4] text-center cursor-pointer transition-all duration-[130ms]"
                        [class]="isSelected(level)
                          ? 'bg-[#357bbf] text-white border-[1.5px] border-transparent shadow-[0_1px_5px_rgba(53,123,191,0.32)] hover:bg-[#2b6db5]'
                          : 'bg-white text-black/60 border-[1.5px] border-black/[0.09] hover:border-[rgba(53,123,191,0.42)] hover:bg-[rgba(53,123,191,0.045)] hover:text-black/80 hover:-translate-y-px'"
                        [title]="level.name"
                        (click)="toggle(level)">
                        {{ level.name }}
                      </button>
                    } @else {
                      <div class="block w-full px-[11px] py-[6px] mb-[5px] rounded-full text-center text-[12.5px] text-black/[0.2] border-[1.5px] border-dashed border-black/[0.07] select-none">—</div>
                    }
                  </div>
                }
              }

              <!-- Bottom padding -->
              @for (region of getRegionsForBand(band); track region) {
                <div class="pb-3"></div>
              }

            </div>
          </div>
        }
      }
    </div>
  `,
})
export class EducationLevelSelectorFieldComponent
  extends FieldType<FieldTypeConfig>
  implements OnInit
{
  private readonly educationLevelService = inject(EducationLevelService);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly bands = BANDS;
  readonly bandStyles = BAND_STYLES;

  private readonly loadedLevels = signal<EducationLevel[]>([]);

  readonly groupedLevels = computed(() => {
    const levels = this.loadedLevels();
    const excluded = (this.props['excludedRegions'] as string[] | undefined) ?? ['EU', 'BR'];

    const groups: Record<string, Record<string, EducationLevel[]>> = {
      [SchoolType.Middelbaar]: {},
      [SchoolType.Vervolgonderwijs]: {},
    };

    for (const ed of levels) {
      const band = ed.schoolType ?? SchoolType.Middelbaar;
      for (const region of ed.regions ?? []) {
        if (excluded.includes(region)) continue;
        groups[band] ??= {};
        groups[band][region] ??= [];
        groups[band][region].push(ed);
      }
    }

    for (const band of Object.keys(groups)) {
      for (const region of Object.keys(groups[band])) {
        groups[band][region].sort((a, b) => (a.level ?? 0) - (b.level ?? 0));
      }
    }

    return groups;
  });

  ngOnInit(): void {
    const loadFn = this.props['loadOptions'] as LoadFn | undefined;

    if (typeof loadFn === 'function') {
      runInInjectionContext(this.injector, () => {
        loadFn({ injector: this.injector })
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((levels) => {
            this.loadedLevels.set(levels ?? []);
            this.cdr.markForCheck();
          });
      });
    } else {
      this.educationLevelService
        .getEducationLevels()
        .pipe(
          map((r) => r?.data ?? []),
          catchError(() => of([])),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe((levels) => {
          this.loadedLevels.set(levels);
          this.cdr.markForCheck();
        });
    }
  }

  getRegionsForBand(band: string): string[] {
    return Object.keys(this.groupedLevels()[band] ?? {});
  }

  /** Sorted list of unique level numbers present across all regions for a band. */
  uniqueLevelsForBand(band: string): number[] {
    const grouped = this.groupedLevels()[band] ?? {};
    const nums = new Set<number>();
    for (const items of Object.values(grouped)) {
      for (const item of items) {
        if (item.level != null) nums.add(item.level);
      }
    }
    return [...nums].sort((a, b) => a - b);
  }

  /** Returns the education level for a specific level number in a region, or undefined. */
  getLevelInRegion(band: string, region: string, levelNum: number): EducationLevel | undefined {
    return (this.groupedLevels()[band]?.[region] ?? []).find((item) => item.level === levelNum);
  }

  selectedCountForBand(band: string): number {
    const current = (this.formControl.value as EducationLevel[]) ?? [];
    const selectedIds = new Set(current.map((l) => l.id));
    const grouped = this.groupedLevels()[band] ?? {};
    const inBand = new Set<string>();
    for (const levels of Object.values(grouped)) {
      for (const level of levels) {
        if (level.id) inBand.add(level.id);
      }
    }
    return [...inBand].filter((id) => selectedIds.has(id)).length;
  }

  isSelected(item: EducationLevel): boolean {
    const current = (this.formControl.value as EducationLevel[]) ?? [];
    return current.some((l) => l.id === item.id);
  }

  toggle(item: EducationLevel): void {
    const current = (this.formControl.value as EducationLevel[]) ?? [];
    const next = current.some((l) => l.id === item.id)
      ? current.filter((l) => l.id !== item.id)
      : [...current, item];
    this.formControl.setValue(next);
    this.formControl.markAsDirty();
    this.cdr.markForCheck();
  }
}
